import tippy, { createSingleton } from 'tippy.js'
import type { Instance, Placement } from 'tippy.js'
import './styles.css'

const THEME_MAP: Record<string, string> = {
    'tooltip-primary': 'tip tip-primary',
    'tooltip-secondary': 'tip tip-secondary',
    'tooltip-accent': 'tip tip-accent',
    'tooltip-info': 'tip tip-info',
    'tooltip-success': 'tip tip-success',
    'tooltip-warning': 'tip tip-warning',
    'tooltip-error': 'tip tip-error',
}

function resolveTheme(el: HTMLElement): string {
    for (const [cls, theme] of Object.entries(THEME_MAP)) {
        if (el.classList.contains(cls)) return theme
    }
    return 'tip'
}

function resolvePlacement(el: HTMLElement): Placement {
    if (el.classList.contains('tooltip-bottom')) return 'bottom'
    if (el.classList.contains('tooltip-left')) return 'left'
    if (el.classList.contains('tooltip-right')) return 'right'
    return 'top'
}

let initialized = false
let overlay: HTMLElement | null = null

function initTooltips(): void {
    if (initialized) return
    initialized = true

    overlay = document.createElement('div')
    overlay.className = 'tippy-overlay'
    document.body.appendChild(overlay)

    const instances: Instance[] = []
    const appendToMap = new Map<HTMLElement, HTMLElement | null>()
    let singletonPopper: HTMLElement | null = null
    let currentEl: HTMLElement | null = null
    let singleton: ReturnType<typeof createSingleton> | null = null

    let mouseX = -1
    let mouseY = -1
    document.addEventListener('mousemove', e => { mouseX = e.clientX; mouseY = e.clientY }, { passive: true })

    // インスタンスを作成して instances に追加（singleton の更新は行わない）
    function initElement(el: HTMLElement): boolean {
        if (el.hasAttribute('data-tooltip-init')) return false
        const content = el.dataset.tip
        if (!content) return false

        el.setAttribute('data-tooltip-init', '')

        const appendToSelector = el.dataset.tipAppendTo
        const appendToEl = appendToSelector
            ? document.querySelector<HTMLElement>(appendToSelector)
            : null

        if (appendToEl && el.hasAttribute('data-tip-full-width')) {
            appendToEl.style.position = 'relative'
        }
        appendToMap.set(el, appendToEl)

        el.addEventListener('mouseenter', () => {
            currentEl = el
            if (el.hasAttribute('data-tip-overlay')) {
                overlay!.classList.add('is-visible')
            } else {
                overlay!.classList.remove('is-visible')
            }
            const isFullWidth = el.hasAttribute('data-tip-full-width')
            singletonPopper?.classList.toggle('tippy-full-width', isFullWidth)
            singletonPopper?.classList.toggle('tippy-custom-container', !!appendToEl)
            if (isFullWidth && singletonPopper && appendToEl) {
                const refRect = el.getBoundingClientRect()
                const containerRect = appendToEl.getBoundingClientRect()
                const arrowLeft = refRect.left + refRect.width / 2 - containerRect.left
                singletonPopper.style.setProperty('--tippy-arrow-left', `${arrowLeft}px`)
            }
        })

        const interactive = el.dataset.tipInteractive === 'true'

        instances.push(tippy(el, {
            content,
            placement: resolvePlacement(el),
            theme: resolveTheme(el),
            arrow: true,
            interactive,
        }) as Instance)

        return true
    }

    // DOMから削除された要素のインスタンスを破棄
    function removeElement(el: HTMLElement): void {
        const idx = instances.findIndex(i => i.reference === el)
        if (idx === -1) return
        instances.splice(idx, 1)
        appendToMap.delete(el)
    }

    // 現在の instances で singleton を作成または更新（バッチ更新用）
    function flushSingleton(): void {
        if (instances.length === 0) {
            if (singleton) {
                singleton.destroy()
                singleton = null
                singletonPopper = null
            }
            return
        }
        if (!singleton) {
            singleton = createSingleton(instances, {
                overrides: ['content', 'placement', 'theme', 'interactive'],
                arrow: true,
                interactive: false,
                moveTransition: 'transform 0s linear 0s',
                appendTo() {
                    const container = currentEl ? appendToMap.get(currentEl) : null
                    return container ?? document.body
                },
                onCreate(inst) { singletonPopper = inst.popper },
                onShow(inst) {
                    if (inst.popper.parentElement === null) return
                    const ref = currentEl ?? inst.reference as HTMLElement
                    const targetContainer = appendToMap.get(ref) ?? document.body
                    if (inst.popper.parentElement !== targetContainer) {
                        targetContainer.appendChild(inst.popper)
                    }
                },
                onHidden() {
                    overlay!.classList.remove('is-visible')
                    singletonPopper?.classList.remove('tippy-full-width')
                },
            })
        } else {
            singleton.setInstances(instances)
        }
    }

    function triggerIfUnderMouse(el: HTMLElement): void {
        if (mouseX < 0) return
        const rect = el.getBoundingClientRect()
        if (mouseX >= rect.left && mouseX <= rect.right &&
            mouseY >= rect.top && mouseY <= rect.bottom) {
            el.dispatchEvent(new MouseEvent('mouseenter', { bubbles: false }))
        }
    }

    // 既存要素を一括初期化 → singleton を1回だけ作成
    document.querySelectorAll<HTMLElement>('[data-tip]').forEach(initElement)
    flushSingleton()

    // ① ホバー時: キャプチャフェーズで initElement し、
    //   singleton の mouseenter がターゲットフェーズで発火するようにする
    document.body.addEventListener('mouseenter', (event) => {
        const el = (event.target as Element).closest<HTMLElement>('[data-tip]')
        if (el && initElement(el)) flushSingleton()
    }, true)

    // ② DOM変化時: バッチ処理で setInstances を1回に削減 + 削除済み要素のクリーンアップ
    new MutationObserver(mutations => {
        const toTrigger: HTMLElement[] = []
        let dirty = false

        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                for (const node of Array.from(mutation.removedNodes)) {
                    if (!(node instanceof HTMLElement)) continue
                    if (node.hasAttribute('data-tooltip-init')) removeElement(node)
                    node.querySelectorAll<HTMLElement>('[data-tooltip-init]').forEach(removeElement)
                    dirty = true
                }
                for (const node of Array.from(mutation.addedNodes)) {
                    if (!(node instanceof HTMLElement)) continue
                    if (node.matches('[data-tip]') && initElement(node)) {
                        dirty = true
                        toTrigger.push(node)
                    }
                    node.querySelectorAll<HTMLElement>('[data-tip]').forEach(el => {
                        if (initElement(el)) { dirty = true; toTrigger.push(el) }
                    })
                }
            } else if (mutation.type === 'attributes') {
                const el = mutation.target as HTMLElement
                if (el.dataset.tip && initElement(el)) { dirty = true; toTrigger.push(el) }
            }
        }

        if (dirty) flushSingleton()
        toTrigger.forEach(triggerIfUnderMouse)
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-tip'] })
}

export { initTooltips }
